import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import './EmployeeDashboard.css';

const today = new Date();
const todayStr = today.toLocaleDateString('en-IN', {weekday:'long', day:'numeric', month:'long'});
const dateKey = today.toISOString().split('T')[0];
const LEAVE_TYPES = ['Casual Leave','Sick Leave','Earned Leave','Maternity Leave'];
const WORK_END_HOUR = 18; // 6 PM cutoff

function pushNotif(msg) {
  api.notifications.create({ message: msg, type: 'activity' }).catch(() => {});
}

function calcHoursStatus(entries) {
  if (!entries || entries.length === 0) return { hours: '—', status: 'Pending' };
  let totalMins = 0;
  let pendingCheckout = false;
  for (let i = 0; i < entries.length; i++) {
    if (entries[i].type === 'in') {
      const next = entries[i+1];
      if (next && next.type === 'out') {
        const [ih, im] = entries[i].time.split(':').map(Number);
        const [oh, om] = next.time.split(':').map(Number);
        totalMins += (oh*60+om) - (ih*60+im);
      } else {
        pendingCheckout = true;
      }
    }
  }
  const hrs = totalMins / 60;
  if (pendingCheckout) return { hours: hrs > 0 ? hrs.toFixed(1)+'h+' : '—', status: 'In Progress' };
  if (hrs >= 8) return { hours: hrs.toFixed(1)+'h', status: 'Full Day' };
  if (hrs >= 4) return { hours: hrs.toFixed(1)+'h', status: 'Half Day' };
  if (hrs > 0) return { hours: hrs.toFixed(1)+'h', status: 'Short' };
  return { hours: '—', status: 'Pending' };
}

const COLUMNS = [
  { id:'todo',       label:'To Do',       color:'#6B7280' },
  { id:'inprogress', label:'In Progress', color:'#3B82F6' },
  { id:'review',     label:'In Review',   color:'#F5A623' },
  { id:'done',       label:'Done',        color:'#22C55E' },
];

function EmpProjectsKanban({ user, pushNotif }) {
  const [filter, setFilter] = useState('mine'); // 'mine' | 'all'
  const [projects, setProjects] = useState([]);
  React.useEffect(() => {
    api.projects.list().then(rows => setProjects(rows.map(p => ({...p, due: p.due_date})))).catch(()=>{});
  }, []);
  const dragging = React.useRef(null);

  const myName = (user?.name || '').trim().toLowerCase();
  const visible = filter === 'mine'
    ? projects.filter(p => (p.assigned_name||p.assigned||'').trim().toLowerCase() === myName)
    : projects;

  const handleDrop = (colId) => {
    if (!dragging.current) return;
    const proj = projects.find(p => p.id === dragging.current);
    if (!proj) return;
    if ((proj.assigned_name||proj.assigned||'').trim().toLowerCase() !== myName) { dragging.current = null; return; }
    setProjects(prev => prev.map(p => p.id === proj.id ? { ...p, status: colId } : p));
    api.projects.update(proj.id, { status: colId }).catch(err => console.error('Failed to update task:', err));
    pushNotif(`${user.name} moved "${proj.title}" to ${COLUMNS.find(c=>c.id===colId)?.label}`);
    dragging.current = null;
  };

  return (
    <div className="emp2-content">
      <div className="emp2-filter-row">
        <button className={`emp2-filter-btn${filter==='mine'?' active':''}`} onClick={()=>setFilter('mine')}>My Tasks</button>
        <button className={`emp2-filter-btn${filter==='all'?' active':''}`} onClick={()=>setFilter('all')}>All Employees</button>
      </div>
      <div className="emp2-kanban">
        {COLUMNS.map(col => {
          const colTasks = visible.filter(p => p.status === col.id);
          return (
            <div key={col.id} className="emp2-kcol" onDragOver={e=>e.preventDefault()} onDrop={()=>handleDrop(col.id)}>
              <div className="emp2-kcol-hd">
                <span className="emp2-kcol-dot" style={{background:col.color}}/>
                <span>{col.label}</span>
                <span className="emp2-kcol-count">{colTasks.length}</span>
              </div>
              {colTasks.map(p => {
                const isMine = (p.assigned_name||p.assigned||'').trim().toLowerCase() === myName;
                return (
                  <div key={p.id} className="emp2-kcard" draggable={isMine} onDragStart={()=>{dragging.current = p.id;}} style={{opacity: isMine?1:0.7, cursor: isMine?'grab':'default'}}>
                    {p.priority && <span className={`emp2-prio ${p.priority.toLowerCase()}`}>{p.priority}</span>}
                    <div className="emp2-kcard-title">{p.title}</div>
                    <div className="emp2-kcard-assignee">{p.assigned_name||p.assigned}{isMine && <span style={{color:'#F5A623'}}> (you)</span>}</div>
                  </div>
                );
              })}
              {colTasks.length === 0 && <div className="emp2-empty" style={{fontSize:11}}>No tasks</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function EmployeeDashboard() {
  const { theme } = useTheme();
  const { user, logout } = useAuth();
  const [tab, setTab] = useState('dashboard');

  const [att, setAtt] = useState({ entries: [] });
  React.useEffect(() => {
    api.attendance.get(dateKey)
      .then(saved => {
        if (saved && saved.entries) setAtt({ entries: saved.entries });
      })
      .catch(() => {});
  }, []);
  const saveAtt = (a) => {
    setAtt(a);
    api.attendance.save(dateKey, a.entries || []).catch(err => console.error('Failed to save attendance:', err));
  };

  const entries = att.entries || [];
  const lastEntry = entries[entries.length - 1];
  const isCheckedIn = lastEntry && lastEntry.type === 'in';

  const [showManual, setShowManual] = useState(false);
  const [manualTime, setManualTime] = useState('');

  const isPastCutoff = () => new Date().getHours() >= WORK_END_HOUR;

  const handleCheckIn = () => {
    if (isPastCutoff()) { setShowManual(true); return; }
    const time = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
    const type = isCheckedIn ? 'out' : 'in';
    const newEntries = [...entries, { type, time }];
    saveAtt({ entries: newEntries });
    pushNotif(`${user.name} ${type==='in'?'checked in':'checked out'} at ${time}`);
  };

  const submitManual = () => {
    if (!manualTime) return;
    const type = isCheckedIn ? 'out' : 'in';
    const newEntries = [...entries, { type, time: manualTime, manual: true }];
    saveAtt({ entries: newEntries });
    pushNotif(`${user.name} added a manual ${type==='in'?'check-in':'check-out'} at ${manualTime} (after working hours)`);
    setShowManual(false);
    setManualTime('');
  };

  // Employee-created tasks
  const [newTaskText, setNewTaskText] = useState('');
  const addOwnTask = () => {
    if (!newTaskText.trim()) return;
    api.projects.add({ title: newTaskText, status: 'todo', priority: 'Medium', assigned: user?.name, selfAdded: true })
      .then(created => setTasks(prev => [{
        id: 'proj-' + created.id, text: created.title, done: false, fromProject: true, status: created.status, priority: created.priority, due: created.due_date,
      }, ...prev]))
      .then(() => pushNotif(`${user.name} added a new task: "${newTaskText}"`))
      .catch(err => console.error('Failed to add task:', err));
    setNewTaskText('');
  };

  const requestDeleteTask = (taskId, taskText) => {
    // Simplified -- employees can request deletion via direct removal since no separate approval table exists yet.
    // The numeric project id is recovered from the 'proj-<id>' prefix used above.
    const rawId = String(taskId).replace(/^proj-/, '');
    pushNotif(`${user.name} requested to delete task "${taskText}" — removed pending admin review`);
    api.projects.remove(rawId)
      .then(() => setTasks(prev => prev.filter(t => t.id !== taskId)))
      .catch(() => {});
    alert('Delete request sent — task removed.');
  };

  // Tasks
  const [tasks, setTasks] = useState([]);
  React.useEffect(() => {
    api.projects.list()
      .then(projects => {
        const myName = (user?.name || '').trim().toLowerCase();
        const myProjectTasks = projects
          .filter(p => (p.assigned_name || '').trim().toLowerCase() === myName)
          .map(p => ({
            id: 'proj-' + p.id,
            text: p.title,
            done: p.status === 'done',
            fromProject: true,
            status: p.status,
            priority: p.priority,
            due: p.due_date,
          }));
        setTasks(myProjectTasks);
      })
      .catch(() => setTasks([]));
  }, [user]);
  const doneCount = tasks.filter(t => t.done).length;

  // Leave
  const [leaveForm, setLeaveForm] = useState({ type: 'Casual Leave', from: '', to: '', reason: '' });
  const [leaveMsg, setLeaveMsg] = useState('');
  const [myLeaves, setMyLeaves] = useState([]);
  React.useEffect(() => {
    api.leave.list().then(setMyLeaves).catch(() => setMyLeaves([]));
  }, []);

  const applyLeave = () => {
    if (!leaveForm.from || !leaveForm.to || !leaveForm.reason.trim()) { setLeaveMsg('Please fill all fields'); return; }
    api.leave.apply({ leaveType: leaveForm.type, from: leaveForm.from, to: leaveForm.to, reason: leaveForm.reason })
      .then(created => {
        setMyLeaves(prev => [created, ...prev]);
        setLeaveMsg('Leave request submitted successfully');
        setLeaveForm({ type: 'Casual Leave', from: '', to: '', reason: '' });
        setTimeout(() => setLeaveMsg(''), 3000);
      })
      .catch(() => setLeaveMsg('Error submitting leave'));
  };

  // Settings
  const [profile, setProfile] = useState({ name: user?.name || '', email: user?.email || '', phone: '' });
  const [pwForm, setPwForm] = useState({ newPw: '', confirm: '' });
  const [pwMsg, setPwMsg] = useState('');
  const [profSaved, setProfSaved] = useState(false);

  const saveProfile = () => {
    setProfSaved(true); setTimeout(() => setProfSaved(false), 2000);
  };
  const updatePassword = () => {
    if (pwForm.newPw.length < 6) { setPwMsg('Password must be at least 6 characters'); return; }
    if (pwForm.newPw !== pwForm.confirm) { setPwMsg('Passwords do not match'); return; }
    api.auth.changePassword(pwForm.newPw)
      .then(() => {
        setPwMsg('Password updated'); setPwForm({ newPw: '', confirm: '' });
        setTimeout(() => setPwMsg(''), 2000);
      })
      .catch(err => setPwMsg(err.message || 'Error updating password'));
  };

  const NAV = [
    { id: 'dashboard',  label: 'My Dashboard' },
    { id: 'tasks',      label: 'Projects' },
    { id: 'attendance', label: 'Attendance' },
    { id: 'leave',      label: 'Leave' },
    { id: 'settings',   label: 'Settings' },
  ];

  const hist = calcHoursStatus(entries);

  return (
    <div className="emp2-wrap" data-theme={theme}>
      <div className="emp2-sb">
        <div className="emp2-brand">
          <div className="emp2-brand-name">RyzenOS</div>
          <div className="emp2-brand-sub">Employee</div>
        </div>
        <div className="emp2-nav">
          {NAV.map(n => (
            <div key={n.id} className={`emp2-nav-item${tab===n.id?' active':''}`} onClick={()=>setTab(n.id)}>{n.label}</div>
          ))}
        </div>
        <div className="emp2-sb-bottom">
          <div className="emp2-user">
            <div className="emp2-user-av">{user?.initials || 'U'}</div>
            <div>
              <div className="emp2-user-name">{user?.name}</div>
              <div className="emp2-user-role">{user?.dept || 'Team'}</div>
            </div>
          </div>
          <div className="emp2-signout" onClick={logout}>Sign out</div>
        </div>
      </div>

      {/* Mobile bottom bar -- mirrors admin pattern */}
      <div className="emp2-bottombar">
        {NAV.map(n => (
          <div key={n.id} className={`emp2-bottombar-item${tab===n.id?' active':''}`} onClick={()=>setTab(n.id)}>
            <span className="emp2-bottombar-label">{n.label === 'My Dashboard' ? 'Home' : n.label}</span>
          </div>
        ))}
      </div>

      <div className="emp2-main">
        <div className="emp2-topbar">
          <div className="emp2-title">{NAV.find(n=>n.id===tab)?.label}</div>
          <div className="emp2-date">{today.toLocaleDateString('en-IN', {weekday:'short', day:'numeric', month:'short'})}</div>
        </div>

        {tab === 'dashboard' && (
          <div className="emp2-content">
            <div className="emp2-stat-row">
              <div className="emp2-stat"><div className="emp2-stat-lbl">Tasks assigned</div><div className="emp2-stat-val" style={{color:'#3B82F6'}}>{tasks.length}</div></div>
              <div className="emp2-stat"><div className="emp2-stat-lbl">Completed</div><div className="emp2-stat-val" style={{color:'#22C55E'}}>{doneCount}</div></div>
              <div className="emp2-stat"><div className="emp2-stat-lbl">Leave balance</div><div className="emp2-stat-val" style={{color:'#F5A623'}}>9 days</div></div>
            </div>
            <div className="emp2-card">
              <div className="emp2-card-hd">Today's tasks</div>
              {tasks.length===0 ? <div className="emp2-empty">No tasks assigned</div> : tasks.slice(0,5).map(t=>(
                <div key={t.id} className={`emp2-task${t.done?' done':''}`}>
                  <span className="emp2-task-dot" style={{background:t.done?'#22C55E':'#F5A623'}}/>
                  <span>{t.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'tasks' && (
          <EmpProjectsKanban user={user} pushNotif={pushNotif}/>
        )}

        {tab === 'attendance' && (
          <div className="emp2-content">
            <div className="emp2-card">
              <div className="emp2-card-hd">Today's attendance</div>
              <div className="emp2-att-box">
                <div className="emp2-att-date">{today.toLocaleDateString('en-IN', {weekday:'long', day:'numeric', month:'long'})}</div>
                {entries.length > 0 && (
                  <div style={{display:'flex',flexWrap:'wrap',gap:6,justifyContent:'center',marginBottom:14}}>
                    {entries.map((e,i)=>(
                      <span key={i} className={`emp2-badge ${e.type==='in'?'amber':'green'}`}>
                        {e.type==='in'?'In':'Out'} {e.time}{e.manual?'*':''}
                      </span>
                    ))}
                  </div>
                )}
                {!showManual ? (
                  <button className="emp2-checkin-btn" onClick={handleCheckIn}>
                    {isCheckedIn ? 'Check Out' : 'Check In'}
                  </button>
                ) : (
                  <div className="emp2-manual-box">
                    <div className="emp2-manual-hint">Past 6 PM — enter time manually</div>
                    <input className="emp2-inp" type="time" value={manualTime} onChange={e=>setManualTime(e.target.value)}/>
                    <div style={{display:'flex',gap:8,marginTop:8}}>
                      <button className="emp2-checkin-btn" style={{flex:1}} onClick={submitManual}>Submit</button>
                      <button className="emp2-cancel-btn" onClick={()=>setShowManual(false)}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="emp2-card">
              <div className="emp2-card-hd-row">
                <div className="emp2-card-hd">Attendance history</div>
                <div className="emp2-hint">9AM–6PM = Full Day | 4h+ = Half Day</div>
              </div>
              <table className="emp2-table">
                <thead><tr>{['Date','Check In','Check Out','Hours','Status'].map(h=><th key={h}>{h}</th>)}</tr></thead>
                <tbody>
                  <tr>
                    <td>{dateKey}</td>
                    <td style={{color:'#F5A623'}}>{entries.find(e=>e.type==='in')?.time || '—'}</td>
                    <td style={{color: entries.filter(e=>e.type==='out').length ? '#22C55E' : '#ef4444'}}>{[...entries].reverse().find(e=>e.type==='out')?.time || 'Pending'}</td>
                    <td>{hist.hours}</td>
                    <td><span className={`emp2-badge ${hist.status==='Full Day'?'green':hist.status==='Half Day'?'amber':hist.status==='Pending'?'red':'gray'}`}>{hist.status}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'leave' && (
          <div className="emp2-content">
            <div className="emp2-two-col">
              <div className="emp2-card">
                <div className="emp2-card-hd">Apply for Leave</div>
                <div className="emp2-form-grp"><label>Leave Type</label>
                  <select className="emp2-inp" value={leaveForm.type} onChange={e=>setLeaveForm(f=>({...f,type:e.target.value}))}>
                    {LEAVE_TYPES.map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="emp2-form-row">
                  <div className="emp2-form-grp"><label>From</label><input className="emp2-inp" type="date" value={leaveForm.from} onChange={e=>setLeaveForm(f=>({...f,from:e.target.value}))}/></div>
                  <div className="emp2-form-grp"><label>To</label><input className="emp2-inp" type="date" value={leaveForm.to} onChange={e=>setLeaveForm(f=>({...f,to:e.target.value}))}/></div>
                </div>
                <div className="emp2-form-grp"><label>Reason</label><textarea className="emp2-inp" rows="3" value={leaveForm.reason} onChange={e=>setLeaveForm(f=>({...f,reason:e.target.value}))}/></div>
                {leaveMsg && <div className={`emp2-msg ${leaveMsg.includes('success')?'ok':'err'}`}>{leaveMsg}</div>}
                <button className="emp2-checkin-btn" style={{width:'100%'}} onClick={applyLeave}>Submit Request</button>
              </div>
              <div className="emp2-card">
                <div className="emp2-card-hd">My Leave Requests</div>
                {myLeaves.length===0 ? <div className="emp2-empty">No leave requests yet</div> : myLeaves.map(l=>(
                  <div key={l.id} className="emp2-leave-item">
                    <div style={{fontWeight:600,fontSize:13}}>{l.type}</div>
                    <div style={{fontSize:12,opacity:.6}}>{l.from} → {l.to}</div>
                    <div style={{fontSize:12,opacity:.6}}>{l.reason}</div>
                    <span className={`emp2-badge ${l.status==='Approved'?'green':l.status==='Declined'?'red':'amber'}`}>{l.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === 'settings' && (
          <div className="emp2-content">
            <div className="emp2-two-col">
              <div className="emp2-card">
                <div className="emp2-card-hd">My Profile</div>
                <div className="emp2-form-grp"><label>Name</label><input className="emp2-inp" value={profile.name} onChange={e=>setProfile(p=>({...p,name:e.target.value}))}/></div>
                <div className="emp2-form-grp"><label>Email</label><input className="emp2-inp" value={profile.email} onChange={e=>setProfile(p=>({...p,email:e.target.value}))}/></div>
                <div className="emp2-form-grp"><label>Phone</label><input className="emp2-inp" placeholder="+91 XXXXX XXXXX" value={profile.phone} onChange={e=>setProfile(p=>({...p,phone:e.target.value}))}/></div>
                <button className="emp2-checkin-btn" onClick={saveProfile}>{profSaved?'✓ Saved':'Save changes'}</button>
              </div>
              <div className="emp2-card">
                <div className="emp2-card-hd">Change Password</div>
                <div className="emp2-form-grp"><label>New password</label><input className="emp2-inp" type="password" value={pwForm.newPw} onChange={e=>setPwForm(f=>({...f,newPw:e.target.value}))}/></div>
                <div className="emp2-form-grp"><label>Confirm password</label><input className="emp2-inp" type="password" value={pwForm.confirm} onChange={e=>setPwForm(f=>({...f,confirm:e.target.value}))}/></div>
                {pwMsg && <div className={`emp2-msg ${pwMsg.includes('updated')?'ok':'err'}`}>{pwMsg}</div>}
                <button className="emp2-checkin-btn" onClick={updatePassword}>Update Password</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
